import { BASE_URL } from "../config/api";
import { authService } from "./authService";

interface ApiClientOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  message?: string;
}

// List of public routes that should not trigger login redirect on 401
const publicRoutes = ["/privacy-policy", "/about", "/login", "/register", "/reset-password"];

// Check if current path is a public route
const isPublicRoute = (path: string): boolean => {
  return publicRoutes.some((route) => path.startsWith(route));
};

class ApiClient {
  private static instance: ApiClient;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  private constructor() {}

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private processQueue(error: any = null) {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
    this.failedQueue = [];
  }

  private async handleUnauthorized() {
    // If we're already refreshing, queue the request
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const currentPath = window.location.pathname + window.location.search;

      // Don't redirect if we're on a public route
      if (isPublicRoute(currentPath)) {
        this.processQueue(new Error("Session expired"));
        return;
      }

      this.processQueue(new Error("Session expired"));
      authService.handleUnauthorized(currentPath);
    } catch (error) {
      this.processQueue(error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async fetch(endpoint: string, options: ApiClientOptions = {}) {
    // Skip auth check for login, signup, and password reset endpoints
    const isPublicEndpoint =
      endpoint.includes("/login") ||
      endpoint.includes("/guest") ||
      endpoint.includes("/signup") ||
      endpoint.includes("/logout");

    const token = authService.getToken();

    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...options.headers,
    };

    if (!isFormData && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (!token && !isPublicEndpoint) {
      const currentPath = window.location.pathname;
      // Don't redirect if we're on a public route or checking auth status
      if (!isPublicRoute(currentPath) && !currentPath.includes("/login") && !endpoint.includes("/auth/me")) {
        window.location.href = "/login";
      }
      throw new Error("No authentication token available");
    }

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: options.method || "GET",
        headers,
        body: options.body
          ? isFormData
            ? options.body
            : JSON.stringify(options.body)
          : undefined,
        credentials: "include",
      });

      if (response.status === 401) {
        await this.handleUnauthorized();
        throw new Error("Session expired");
      }
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          await this.handleUnauthorized();
          throw new Error("Session expired");
        }

        const error = await response.json();
        throw new Error(error.message || "Request failed");
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        console.log("error.message1111==>", error.message);
        if (error.message === "No authentication token available") {
          throw new Error(
            "Email or password is incorrect. If you need further help, email luminous@rupertspira.com"
          );
        }
        if (error.message === "Session expired") {
          throw error;
        }
        if (error.message === "Failed to fetch") {
          throw new Error(
            "Unable to connect to server. Please check your internet connection."
          );
        }
        if (
          error.message ===
          "An account with this email address already exists. Please use a different email or try logging in."
        ) {
          throw new Error(
            "This email address is already registered. Please try a different one."
          );
        }
        if (
          error.message ===
          "This email address is already registered. Please try a different one."
        ) {
          throw new Error(
            "This email address is already registered. Please try a different one."
          );
        }
        throw error;
      }
      throw new Error("Unable to process your request. Please try again.");
    }
  }
}

export const apiClient = ApiClient.getInstance();
